import { useMemo } from 'react';

import type { SectionLockView } from '@fireflink/resource-import';

import { useActiveProject } from '@/hooks/useActiveProject';
import { getActiveProjectId } from '@/lib/utils/currentProject';
import { useAppSelector } from '@/store/hooks';
import type { SectionLock } from '@/store/globalLock/globalLockSlice';

export const useSectionLocks = (): Record<string, SectionLockView> => {
  const sections = useAppSelector(state => state.globalLock?.sections) as
    Record<string, SectionLock> | undefined;
  const { activeProjectId } = useActiveProject();

  return useMemo(() => {
    if (!sections) {
      return {};
    }
    // Not every project switch fires CURRENT_PROJECT_CHANGE_EVENT, so read the
    // store directly; activeProjectId only drives recomputation on the event.
    const currentProjectId = getActiveProjectId() ?? activeProjectId;
    return Object.fromEntries(
      Object.entries(sections)
        .filter(([, lock]) => lock.projectId === currentProjectId)
        .map(([sectionType, lock]) => [
          sectionType,
          {
            isLocked: true,
            projectId: lock.projectId,
            sessionId: lock.sessionId,
            userName: lock.userName,
            lockType: lock.lockType,
          },
        ]),
    );
  }, [sections, activeProjectId]);
};
